/**
 * The welcome pack the super admin sends to a new family user: one email (and
 * the same as an A4 sheet to print) with everything needed to start, in
 * English, Bangla or both:
 *   - Website: the set-password link (works once, until it expires), then the
 *     address to sign in, and what to do first.
 *   - Phone app: where to get it, the phone user name and password, and the
 *     first steps (sign in, allow reminders, mark Taken).
 * Made by Super admin → Families → "New user + welcome email" (AdminWelcome.tsx).
 */
import { fmtDMYTime } from './ui/dates';

export type WelcomeLang = 'en' | 'bn' | 'both';

export interface WelcomeInput {
  lang: WelcomeLang;
  name: string;
  familyName: string;
  adminName?: string;
  /** Website login (omitted when only a phone login was made). */
  /** link = set-password link; tempPassword = one-time password (sign in, then choose your own). */
  web?: { email: string; link?: string; tempPassword?: string; expiresAt: string };
  /** A new family with nobody in it yet: tell the owner to add the first person. */
  newFamily?: boolean;
  /** Phone app login (omitted when only a website login was made). */
  phone?: { username: string; password: string; forPeople: string };
  site: string;
  manual: string;
  /** Where to download the phone app (APK link); empty = "ask your family". */
  appLink: string;
}

const BN_DIGITS = '০১২৩৪৫৬৭৮৯';
const bnNum = (s: string) => s.replace(/[0-9]/g, d => BN_DIGITS[Number(d)]);

interface Section { title: string; steps: string[] }
interface Pack { subject: string; hello: string; intro: string; sections: Section[]; help: string; sign: string }

function en(o: WelcomeInput): Pack {
  const s: Section[] = [];
  if (o.web) s.push({ title: 'Website (for managing the family\'s medicines)', steps: [
    ...(o.web.tempPassword ? [
      `Sign in at ${o.site}\nEmail: ${o.web.email}\nOne-time password: ${o.web.tempPassword}\nIt works until ${fmtDMYTime(o.web.expiresAt)}. Right after signing in you choose your own password (at least 8 characters).`,
    ] : [
      `Set your password: open this link and choose a password (at least 8 characters). It works once, until ${fmtDMYTime(o.web.expiresAt)}:\n${o.web.link}`,
      `After that, sign in at ${o.site} with your email ${o.web.email} and your password.`,
    ]),
    ...(o.newFamily ? ['First, add the person whose medicines you look after (for example your mother): open the person menu at the top and choose "+ Add person".'] : []),
    `You are in the family "${o.familyName}". Add medicines on the Purchase Calculator (+ Add Medicine); reminders, doctor visits and tests have their own tabs.`,
    `On a phone, open the website in Chrome and choose ⋮ → Add to Home screen to use it like an app.`,
  ] });
  if (o.phone) s.push({ title: `Phone app (for ${o.phone.forPeople}: medicine reminders)`, steps: [
    o.appLink ? `Install the Bhorosha app from this link (Android): ${o.appLink}\nIf the phone asks, allow "Install unknown apps". If Play Protect warns, tap More details → Install anyway.`
      : 'Install the Bhorosha app (Android): ask your family for the download link.',
    `Open Bhorosha and sign in. No email is needed:\nUser name: ${o.phone.username}\nPassword: ${o.phone.password}`,
    'Tap "Allow reminders" and then Allow. When it shows "Reminders are on", send a test reminder to check.',
    'Every day: when a reminder rings, take the medicines and tap ✓ TAKEN. Before-meal and after-meal medicines are shown separately.',
    'Blood pressure: open the BP tab, type the top and bottom numbers and save. Settings → Alerts turns each kind of alert on or off.',
  ] });
  return {
    subject: 'Welcome to Bhorosha: how to start',
    hello: `Hi ${o.name || 'there'},`,
    intro: `You now have ${o.web && o.phone ? 'a Bhorosha website login and a phone app login' : o.web ? 'a Bhorosha website login' : 'a Bhorosha phone app login'} for the family "${o.familyName}". Bhorosha keeps the family's medicines, reminders, doctor visits and tests in one place.`,
    sections: s,
    help: `Short guide: ${o.manual}\nKeep the password private. If a link has expired or something doesn't work, reply to this email.`,
    sign: `${o.adminName ? `— ${o.adminName}, ` : '— '}Bhorosha admin`,
  };
}

function bn(o: WelcomeInput): Pack {
  const s: Section[] = [];
  if (o.web) s.push({ title: 'ওয়েবসাইট (পরিবারের ওষুধ দেখাশোনার জন্য)', steps: [
    ...(o.web.tempPassword ? [
      `${o.site} -এ লগইন করুন\nইমেইল: ${o.web.email}\nএককালীন পাসওয়ার্ড: ${o.web.tempPassword}\nএটি ${bnNum(fmtDMYTime(o.web.expiresAt))} পর্যন্ত কাজ করবে। লগইনের পরই নিজের পাসওয়ার্ড ঠিক করে নিন (কমপক্ষে ৮ অক্ষর)।`,
    ] : [
      `পাসওয়ার্ড ঠিক করুন: এই লিংকটি খুলে একটি পাসওয়ার্ড দিন (কমপক্ষে ৮ অক্ষর)। লিংকটি একবারই কাজ করবে, ${bnNum(fmtDMYTime(o.web.expiresAt))} পর্যন্ত:\n${o.web.link}`,
      `এরপর ${o.site} -এ আপনার ইমেইল ${o.web.email} ও পাসওয়ার্ড দিয়ে লগইন করুন।`,
    ]),
    ...(o.newFamily ? ['প্রথমে যাঁর ওষুধ দেখাশোনা করবেন (যেমন মা) তাঁকে যোগ করুন: উপরের ব্যক্তির মেনু খুলে "+ Add person" বেছে নিন।'] : []),
    `আপনি "${o.familyName}" পরিবারে আছেন। Purchase Calculator-এ (+ Add Medicine) ওষুধ যোগ করুন; রিমাইন্ডার, ডাক্তার ও টেস্টের আলাদা ট্যাব আছে।`,
    `মোবাইলে Chrome-এ ওয়েবসাইটটি খুলে ⋮ → Add to Home screen দিলে অ্যাপের মতো ব্যবহার করা যাবে।`,
  ] });
  if (o.phone) s.push({ title: `ফোন অ্যাপ (${o.phone.forPeople}-এর ওষুধের রিমাইন্ডার)`, steps: [
    o.appLink ? `এই লিংক থেকে ভরসা অ্যাপ ইনস্টল করুন (Android): ${o.appLink}\nফোন "Install unknown apps" চাইলে Allow দিন। Play Protect সতর্ক করলে More details → Install anyway চাপুন।`
      : 'ভরসা অ্যাপ ইনস্টল করুন (Android): ডাউনলোড লিংকের জন্য পরিবারের সঙ্গে যোগাযোগ করুন।',
    `ভরসা খুলে লগইন করুন। ইমেইল লাগবে না:\nইউজার নেম: ${o.phone.username}\nপাসওয়ার্ড: ${o.phone.password}`,
    '"রিমাইন্ডার চালু করুন" চাপুন, তারপর Allow। "রিমাইন্ডার চালু হয়েছে" দেখালে একটি পরীক্ষামূলক রিমাইন্ডার পাঠিয়ে দেখুন।',
    'প্রতিদিন: রিমাইন্ডার বাজলে ওষুধ খেয়ে ✓ খেয়েছি চাপুন। খাওয়ার আগের ও পরের ওষুধ আলাদা দেখাবে।',
    'রক্তচাপ: রক্তচাপ ট্যাবে উপরের ও নিচের সংখ্যা লিখে সেভ করুন। সেটিংস → সতর্কবার্তা থেকে কোনগুলো বাজবে চালু বা বন্ধ করা যায়।',
  ] });
  return {
    subject: 'ভরসায় স্বাগতম: কীভাবে শুরু করবেন',
    hello: `প্রিয় ${o.name || 'ব্যবহারকারী'},`,
    intro: `"${o.familyName}" পরিবারের জন্য আপনার ${o.web && o.phone ? 'ভরসা ওয়েবসাইট ও ফোন অ্যাপের লগইন' : o.web ? 'ভরসা ওয়েবসাইটের লগইন' : 'ভরসা ফোন অ্যাপের লগইন'} তৈরি হয়েছে। ভরসায় পরিবারের ওষুধ, রিমাইন্ডার, ডাক্তার দেখানো ও টেস্ট এক জায়গায় থাকে।`,
    sections: s,
    help: `ছোট নির্দেশিকা: ${o.manual}\nপাসওয়ার্ড কাউকে দেবেন না। লিংকের মেয়াদ শেষ হলে বা কোনো সমস্যা হলে এই ইমেইলের উত্তর দিন।`,
    sign: `${o.adminName ? `— ${o.adminName}, ` : '— '}ভরসা অ্যাডমিন`,
  };
}

function packText(p: Pack): string {
  return [p.hello, '', p.intro, '',
    ...p.sections.flatMap(sec => [sec.title.toUpperCase(), ...sec.steps.map((st, i) => `${i + 1}. ${st}`), '']),
    p.help, '', p.sign].join('\n');
}

/** The email: subject + plain text body (both languages: Bangla first, then English). */
export function welcomeEmail(o: WelcomeInput) {
  const packs = o.lang === 'en' ? [en(o)] : o.lang === 'bn' ? [bn(o)] : [bn(o), en(o)];
  const subject = o.lang === 'both' ? `${packs[0].subject} / ${packs[1].subject}` : packs[0].subject;
  const body = packs.map(packText).join('\n\n────────────────────\n\n');
  return { subject, body, full: `Subject: ${subject}\n\n${body}` };
}

const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const linkify = (s: string) => esc(s).replace(/(https?:\/\/[^\s<]+)/g, '<span class="u">$1</span>').replace(/\n/g, '<br>');

/**
 * The same as an A4 sheet (HTML page that prints on A4). QR codes (data URLs)
 * are optional: website set-password link, app download link, phone sign-in.
 */
export function welcomeSheet(o: WelcomeInput, qr: { web?: string; app?: string; phone?: string } = {}) {
  const packs = o.lang === 'en' ? [en(o)] : o.lang === 'bn' ? [bn(o)] : [bn(o), en(o)];
  const qrBox = (img: string | undefined, label: string) => img ? `<div class="qr"><img src="${img}" alt=""><div>${esc(label)}</div></div>` : '';
  const page = (p: Pack, l: 'en' | 'bn') => `
    <section class="page ${l}">
      <header><div class="logo">💊</div><div><h1>${l === 'bn' ? 'ভরসা' : 'Bhorosha'}</h1><div class="sub">${esc(p.subject)}</div></div></header>
      <p class="hello">${esc(p.hello)}</p><p>${esc(p.intro)}</p>
      ${p.sections.map((sec, si) => `<div class="sec"><h2>${esc(sec.title)}</h2>
        <div class="qrs">${si === 0 && o.web ? qrBox(qr.web, o.web.tempPassword ? (l === 'bn' ? 'ওয়েবসাইট' : 'Website') : (l === 'bn' ? 'পাসওয়ার্ড ঠিক করার লিংক' : 'Set-password link')) : ''}${(o.web ? si === 1 : si === 0) && o.phone ? qrBox(qr.app, l === 'bn' ? 'অ্যাপ ডাউনলোড' : 'Get the app') + qrBox(qr.phone, l === 'bn' ? 'অ্যাপে ক্যামেরা দিয়ে লগইন' : 'Scan to sign in in the app') : ''}</div>
        <ol>${sec.steps.map(st => `<li>${linkify(st)}</li>`).join('')}</ol></div>`).join('')}
      <p class="help">${linkify(p.help)}</p><p class="sign">${esc(p.sign)}</p>
    </section>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(packs[0].subject)}</title><style>
    @page { size: A4; margin: 14mm; }
    body { font-family: "Noto Sans Bengali", "Segoe UI", Arial, sans-serif; color: #1F2A3A; margin: 0; font-size: 12.5px; line-height: 1.5; }
    .page { page-break-after: always; } .page:last-child { page-break-after: auto; }
    header { display: flex; align-items: center; gap: 12px; border-bottom: 3px solid #4A6FA5; padding-bottom: 8px; margin-bottom: 10px; }
    .logo { font-size: 30px; } h1 { margin: 0; font-size: 24px; color: #2E4F86; } .sub { color: #5E6B7D; font-size: 14px; }
    .hello { font-weight: 700; margin-bottom: 2px; }
    .sec { border: 1.5px solid #D3DBE6; border-radius: 12px; padding: 8px 14px 4px; margin: 10px 0; overflow: hidden; }
    h2 { font-size: 15px; margin: 2px 0 4px; color: #2E4F86; }
    ol { margin: 4px 0 6px; padding-left: 20px; } li { margin: 4px 0; }
    .u { color: #2E4F86; word-break: break-all; font-weight: 600; }
    .qrs { float: right; display: flex; gap: 8px; margin: 0 0 6px 10px; }
    .qr { text-align: center; font-size: 10px; color: #5E6B7D; width: 104px; } .qr img { width: 100px; height: 100px; display: block; margin: 0 auto 2px; }
    .help { background: #F3F6FA; border-radius: 10px; padding: 8px 12px; } .sign { color: #5E6B7D; }
  </style></head><body>${packs.map((p, i) => page(p, o.lang === 'en' ? 'en' : i === 0 ? 'bn' : 'en')).join('')}
  <script>window.onload = () => setTimeout(() => window.print(), 300);</script></body></html>`;
}
